import { LoadingFullPageOverlay } from "@/components/Loading"
import {
  NotificationsContext,
  UpdateUserContext,
  UpgradeModalContext,
  VibratoClientContext
} from "@/components/dashboard/contexts"
import { TaskRepo } from "@/lib/tasks"
import { Call, Plan, VibratoClient } from "@/lib/vibrato-api"
import { Modal, ModalBody, ModalFooter, ModalHeader } from "flowbite-react"
import { Calendar, Check, EditPencil, Phone, PhoneOutcome } from "iconoir-react"
import React, { useContext, useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import DemoCallModal from "../DemoCallModal"
import VerifyPhoneNumberModal from "../VerifyPhoneNumberModal"
import CallSettings from "./CallSettings"

export default function NewCall(): React.JSX.Element {
  const navigate = useNavigate()
  const { callId, gptsCallUuid } = useParams()
  const vibratoClient = useContext(VibratoClientContext)
  const setUser = useContext(UpdateUserContext)
  const [call, setCall] = useState<any | null>(null)
  const [taskRepo, setTaskRepo] = useState<any | null>(null)
  const [step, setStep] = useState<string>("select-task")
  const notifications = useContext(NotificationsContext)
  const [showDemoModal, setShowDemoModal] = useState(false)
  const [showDynamicTaskModal, setShowDynamicTaskModal] = useState(false)
  const [editableTask, setEditableTask] = useState(call?.dynamic_task?.task || "")
  const [editableTaskShort, setEditableTaskShort] = useState(call?.dynamic_task?.task_short || "")

  // For steps card
  const [taskName, setTaskName] = useState<string | null>(null)
  const [plan, setPlan] = useState<Plan | null>(null)
  const upgradeModal = useContext(UpgradeModalContext)
  const [showScheduleModal, setShowScheduleModal] = useState(false)
  const [scheduledAt, setScheduledAt] = useState<string | null>(null)

  const [showVerifyPhoneModal, setShowVerifyPhoneModal] = useState(false)

  // Add this state near the other useState declarations at the top
  const [isPlacingCall, setIsPlacingCall] = useState(false)

  // Check for demo=true URL parameter
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search)
    if (searchParams.get("demo") === "true") {
      setShowDemoModal(true)
    }
  }, [])

  useEffect(() => {
    vibratoClient
      .getPlan()
      .then((plan: Plan) => {
        setPlan(plan)
      })
      .catch((error: any) => {
        console.error("Error fetching user plan:", error)
      })
  }, [vibratoClient])

  useEffect(() => {
    if (plan == null) {
      return
    }

    const isLowOnMinutes =
      (plan.minutes_remaining != null && plan.minutes_remaining <= 0) ||
      (plan.minutes_per_day_remaining != null && plan.minutes_per_day_remaining <= 0) ||
      (plan.minutes_per_month_remaining != null && plan.minutes_per_month_remaining <= 0) ||
      (plan.calls_per_day_remaining != null && plan.calls_per_day_remaining <= 0)

    if (isLowOnMinutes) {
      upgradeModal.setShowModal(true)

      let errorMessage = (
        <p>
          You have run out of call minutes. Upgrade now to continue making calls, or{" "}
          <a
            href="#"
            className="text-blue-500 hover:underline"
            onClick={(e) => {
              e.preventDefault()
              upgradeModal.setShowModal(false)
              setShowDemoModal(true)
            }}
          >
            try a free demo call.
          </a>
        </p>
      )

      if (plan.plan_slug === "plus") {
        errorMessage = (
          <p>
            You have reached your call minute limit. The Plus plan is for non-commercial use only. To continue using
            Vibrato for business purposes, please upgrade to one of our Business plans.
          </p>
        )
      }

      upgradeModal.setError(errorMessage)
      return
    }
  }, [plan])

  useEffect(() => {
    if (step == "select-task") {
      document.title = "Vibrato - Explore"
    } else {
      document.title = "Vibrato - New Call"
    }
  }, [step])

  useEffect(() => {
    async function loadTasks(): Promise<void> {
      if (vibratoClient == null) {
        return
      }

      const repo = new TaskRepo(vibratoClient)
      await repo.init()

      setTaskRepo(repo)
    }

    void loadTasks()
  }, [vibratoClient])

  useEffect(() => {
    if (gptsCallUuid != null) {
      vibratoClient
        .createCallFromGptsCall(gptsCallUuid)
        .then((call: any) => {
          navigate(`/calls/${call.id}/edit/`)
        })
        .catch((error: any) => {
          console.error("Error creating call from GPTS call", error)
          notifications({
            type: "error",
            header: "Error creating ChatGPT call",
            id: "gpts-error",
            message: "Call was already created."
          })
          navigate("/new-call")
        })
      return
    }

    if (callId == null && step == "select-task") {
      navigate("/call-library")
      return
    }

    if (callId != null && taskRepo != null) {
      vibratoClient.getCall(callId).then((call: any) => {
        if (call.status != "created") {
          navigate(`/calls/${call.id}/`)
        }

        setStep("configure-call")
        setCall(call)
        setTaskName(call.dynamic_task ? call.dynamic_task.task_short : call.task.name)
        setScheduledAt(call.scheduled_at)
      })
    }

    return () => {
      setCall(null)
      setTaskRepo(null)
    }
  }, [vibratoClient, callId, taskRepo])

  useEffect(() => {
    if (call && call.dynamic_task) {
      setEditableTask(call.dynamic_task.task)
      setEditableTaskShort(call.dynamic_task.task_short)
    }
    return () => {
      setEditableTask(null)
      setEditableTaskShort(null)
    }
  }, [call])

  const showLoading = call == null && taskRepo == null

  return (
    <div className="h-full w-full flex flex-row gap-20 place-content-evenly">
      {showLoading && <LoadingFullPageOverlay />}
      <DemoCallModal showModal={showDemoModal} setShowModal={setShowDemoModal} />
      <VerifyPhoneNumberModal
        show={showVerifyPhoneModal}
        setShow={setShowVerifyPhoneModal}
        onClose={() => setShowVerifyPhoneModal(false)}
        onPhoneNumberVerified={async () => {
          // Update both user session and plan after phone verification
          const session = await vibratoClient.getUserApiSession()
          setUser(session)

          // Refresh plan to get updated needs_phone_verification status
          const updatedPlan = await vibratoClient.getPlan()
          setPlan(updatedPlan)
        }}
      />
      {callId != null && step === "configure-call" && call != null && (
        <div className="w-full flex flex-col items-center bg-gray-50 dark:bg-gray-900">
          <div className="w-full p-4 md:p-6 h-[calc(100%-80px)] overflow-y-auto">
            <div className="mb-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-purple-100 rounded-xl">
                  <PhoneOutcome width="24" className="text-purple-600" />
                </div>
                <div>
                  <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">Configure Your Call</h1>
                  <p className="text-gray-600 dark:text-gray-400">
                    Customize your call settings below before placing or scheduling your call.
                  </p>
                </div>
              </div>
            </div>

            <div className="">
              <CallSettings
                callId={call.id}
                initialCall={call}
                showScheduleModal={showScheduleModal}
                setShowScheduleModal={setShowScheduleModal}
                scheduledAt={scheduledAt}
                setScheduledAt={setScheduledAt}
                setShowVerifyPhoneModal={setShowVerifyPhoneModal}
                setIsPlacingCall={setIsPlacingCall}
                setShowDynamicTaskModal={setShowDynamicTaskModal}
                plan={plan}
              />
            </div>
          </div>
          <div className="w-full h-[80px]">
            <CallFooter
              taskName={taskName}
              call={call}
              setShowScheduleModal={setShowScheduleModal}
              isPlacingCall={isPlacingCall}
              scheduledAt={scheduledAt}
            />
          </div>
        </div>
      )}

      {showDynamicTaskModal && (
        <DynamicTaskModal
          show={showDynamicTaskModal}
          onClose={() => setShowDynamicTaskModal(false)}
          call={call}
          setCall={setCall}
          editableTask={editableTask}
          setEditableTask={setEditableTask}
          editableTaskShort={editableTaskShort}
          setEditableTaskShort={setEditableTaskShort}
        />
      )}
    </div>
  )
}

function CallFooter({
  taskName,
  call,
  setShowScheduleModal,
  isPlacingCall,
  scheduledAt
}: {
  taskName: string | null
  call: any | null
  setShowScheduleModal: (show: boolean) => void
  isPlacingCall: boolean
  scheduledAt: string | null
}): React.JSX.Element {
  return (
    <div className="w-full h-full bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800 p-4 shadow-lg z-50 flex flex-col justify-center">
      <div className="flex flex-col md:flex-row justify-between items-center gap-0 md:gap-4">
        <div className="overflow-x-hidden flex flex-col md:flex-row items-center gap-4">
          {!scheduledAt && !call?.task_template && (
            <div className="hidden md:flex items-center gap-2 overflow-x-hidden">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 32 32" fill="none">
                <circle cx="16" cy="16" r="15.5" stroke={taskName != null ? "#2D27B7" : "gray"} />
                {taskName != null && (
                  <g transform="translate(4, 4)">
                    <Check color="#2D27B7" />
                  </g>
                )}
              </svg>
              <div className="overflow-x-hidden">
                <p className="text-sm text-gray-600 dark:text-gray-400">Task</p>
                {taskName && <p className="text-sm text-gray-900 dark:text-white overflow-x-hidden">{taskName}</p>}
                {!taskName && <p className="text-sm text-gray-400">Not selected</p>}
              </div>
            </div>
          )}

          {!scheduledAt && call?.task_template && (
            <div className="hidden md:flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 32 32" fill="none">
                <circle cx="16" cy="16" r="15.5" stroke={taskName != null ? "#2D27B7" : "gray"} />
                {taskName != null && (
                  <g transform="translate(4, 4)">
                    <Check color="#2D27B7" />
                  </g>
                )}
              </svg>
              <div className="overflow-hidden">
                <p className="text-sm text-gray-600 dark:text-gray-400">Task</p>
                <p className="text-sm text-gray-900 dark:text-white truncate">{call.task_template.task_name}</p>
              </div>
            </div>
          )}

          {scheduledAt && (
            <div className="items-center gap-2 hidden md:flex">
              <Calendar className="text-[#2D27B7]" />
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Scheduled For</p>
                <p className="text-sm text-gray-900 dark:text-white">
                  {new Date(scheduledAt).toLocaleString(undefined, {
                    dateStyle: "medium",
                    timeStyle: "short"
                  })}
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="max-w-max flex gap-2">
          <button
            type="button"
            className="min-w-fit max-h-min bg-linear-to-r from-[#C338F3] to-[#2D27B7] hover:opacity-90 text-white py-2 px-6 rounded-full flex items-center gap-2 transition-all duration-200"
            onClick={() => setShowScheduleModal(true)}
          >
            <Calendar />
            <span>{scheduledAt ? "Edit Schedule" : "Schedule"}</span>
          </button>
          <button
            className="min-w-fit max-h-min bg-linear-to-r from-[#04C48D] to-[#0C7] hover:opacity-90 text-white py-2 px-6 rounded-full flex items-center gap-2 transition-all duration-200 disabled:opacity-50"
            form="callSettingsForm"
            type="submit"
            disabled={isPlacingCall}
          >
            {isPlacingCall ? (
              <>
                <svg
                  className="animate-spin h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                {scheduledAt ? <span>Scheduling Call...</span> : <span>Placing Call...</span>}
              </>
            ) : (
              <>
                <Phone />
                <span>{scheduledAt ? "Schedule Call" : "Place Call"}</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

interface DynamicTaskModalProps {
  show: boolean
  onClose: () => void
  call: Call
  setCall: (call: Call) => void
  editableTask: string
  setEditableTask: (task: string) => void
  editableTaskShort: string
  setEditableTaskShort: (taskShort: string) => void
}

function DynamicTaskModal({
  show,
  onClose,
  call,
  setCall,
  editableTask,
  setEditableTask,
  editableTaskShort,
  setEditableTaskShort
}: DynamicTaskModalProps): React.JSX.Element {
  const vibratoClient: VibratoClient = useContext(VibratoClientContext)

  const handleSave = async () => {
    // Update local state
    setCall({
      ...call,
      dynamic_task: {
        ...call.dynamic_task,
        task: editableTask,
        task_short: editableTaskShort
      }
    })

    // Update on server and refresh call data
    try {
      await vibratoClient.updateDynamicTask(call.dynamic_task.id, {
        task: editableTask,
        task_short: editableTaskShort
      })

      // Fetch updated call data
      const updatedCall = await vibratoClient.getCall(call.id)
      setCall(updatedCall)
    } catch (error) {
      console.error("Error updating dynamic task:", error)
    }

    onClose()
  }

  return (
    <Modal show={show} onClose={onClose} size="4xl" className="pt-safe">
      <ModalHeader className="border-b border-gray-200 bg-linear-to-r from-blue-50 to-purple-50">
        <div className="flex items-center">
          <EditPencil className="text-blue-600 mr-2" />
          <h3 className="text-lg font-semibold text-gray-900">Edit Task</h3>
        </div>
      </ModalHeader>
      <ModalBody className="p-6">
        <div className="space-y-6">
          <div>
            <label htmlFor="task-name" className="block text-sm font-medium text-gray-700 mb-1">
              Task Name
            </label>
            <div className="relative">
              <input
                id="task-name"
                type="text"
                className="w-full p-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                value={editableTaskShort}
                onChange={(e) => setEditableTaskShort(e.target.value)}
                placeholder="Enter a concise task name"
              />
            </div>
            <p className="mt-1 text-xs text-gray-500">This will appear in the call summary and history</p>
          </div>

          <div>
            <label htmlFor="task-details" className="block text-sm font-medium text-gray-700 mb-1">
              Task Details <span className="text-blue-600 font-normal">(Markdown Supported)</span>
            </label>
            <div className="relative">
              <textarea
                id="task-details"
                className="w-full p-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                rows={8}
                value={editableTask}
                onChange={(e) => setEditableTask(e.target.value)}
                placeholder="Describe the task in detail. Use markdown for formatting if needed."
              />
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Provide detailed instructions for the AI to follow during the call
            </p>
          </div>
        </div>
      </ModalBody>
      <ModalFooter className="border-t border-gray-200 p-4 flex flex-col sm:flex-row gap-3 bg-gray-50">
        <button
          type="button"
          onClick={handleSave}
          className="flex-1 justify-center rounded-lg border border-transparent px-4 py-2.5 bg-linear-to-r from-blue-600 to-purple-600 text-base font-medium text-white hover:from-blue-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 flex items-center gap-2"
        >
          <Check width={18} height={18} />
          Save Changes
        </button>
        <button
          type="button"
          onClick={onClose}
          className="flex-1 justify-center rounded-lg border border-gray-300 px-4 py-2.5 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200"
        >
          Cancel
        </button>
      </ModalFooter>
    </Modal>
  )
}
